import { ListingManagerUrlParameters } from '../../models/ListingManagerUrlParameters';
import { ManheimLoginQueryParams } from '../../models/ManheimLoginQueryParams';
import { APP_CONFIG } from '../APP_CONFIG';
import { routeHelper } from './routeHelper';

/**
 * All routes used in the application should be defined here.
 *
 * The goal is to make tracking and updating routes easy because they all live in one place.
 */
export const ROUTES = {
  appShell: {
    developer: routeHelper.plain('/developer'),
    oauthCallback: routeHelper.withParams<{ code?: string; state?: string }>('/oauth_callback'),
    logout: routeHelper.plain('/logout'),
    root: routeHelper.plain('/')
  },
  addVehicle: {
    base: routeHelper.plain('/addVehicle'),
    decodeVin: routeHelper.withParams<{ vin?: string; sellerNumber?: string }>('/addVehicle'),
    vehicleInformation: routeHelper.withParams<{ vin: string; sellerNumber: string }>(
      '/addVehicle/:vin/:sellerNumber/vehicleInformation'
    ),
    valuation: routeHelper.withParams<{ unitGuid: string }>('/addVehicle/:unitGuid/valuation'),
    listingOptions: routeHelper.withParams<{ unitGuid: string; addDetails?: string }>(
      '/addVehicle/:unitGuid/listingOptions'
    ),
    addImages: routeHelper.withParams<{
      unitGuid: string;
      inspectionType?: string;
      continueUri?: string;
      continueLabel?: string;
    }>('/addVehicle/:unitGuid/images'),
    disclosures: routeHelper.withParams<{ unitGuid: string; inspectionType?: string }>(
      '/addVehicle/:unitGuid/disclosures'
    ),
    announcements: routeHelper.withParams<{ unitGuid: string; inspectionType?: string }>(
      '/addVehicle/:unitGuid/announcements'
    ),
    reenter: routeHelper.withParams<{ vin: string; sellerNumber: string; addDetails?: string }>(
      '/addVehicle/:vin/:sellerNumber/reenter'
    )
  },
  listingManager: {
    listingManager: routeHelper.withParams<ListingManagerUrlParameters>('/listingManager')
  },
  sales: {
    base: routeHelper.plain('/sales'),
    runlistBlock: routeHelper.withParams<{
      accountGroupId: string;
      auctionId: string;
      saleNumber: string;
      saleYear: string;
      laneNumber: string;
      startRun: string;
      endRun: string;
      saleDate: string;
    }>('/runListBlock/:accountGroupId/:auctionId/:saleNumber/:saleYear/:laneNumber/:startRun/:endRun/:saleDate')
  },
  auctionRuns: {
    auctionRuns: routeHelper.plain('/view_runs')
  },
  admin: {
    accountSelect: routeHelper.withParams<{ backUrl?: string }>('/admin'),
    accountGroup: {
      list: routeHelper.plain('/admin/accountRollup'),
      edit: routeHelper.withParams<{ id: string }>('/admin/accountRollup/:id/edit')
    },
    accountGroupConfig: {
      list: routeHelper.plain('/admin/accountGroupConfig'),
      edit: routeHelper.withParams<{ id: string }>('/admin/accountGroupConfig/:id/edit')
    },
    betaUsers: routeHelper.plain('/admin/beta_users'),
    impersonationRedirect: routeHelper.withParams<{ username?: string; sellerNumbers?: string; redirectUri: string }>(
      '/admin/redirect'
    )
  },
  statistics: {
    accountStatistics: routeHelper.plain('/statistics')
  },
  csvUpload: {
    base: routeHelper.plain('/csvUpload')
  },
  external: {
    mmr: {
      fullMMR: routeHelper.externalWithParams<{
        country: string;
        mid: string;
        vin: string;
        color: string;
        mileage: string;
        grade?: string;
      }>(`${APP_CONFIG.shared.routes.mmr.baseUrl}`)
    },
    autocheck: {
      url: APP_CONFIG.shared.routes.autocheck.baseUrl
    },
    gfbManagement: {
      manageGfbRedemptions: routeHelper.external(APP_CONFIG.shared.routes.gfbManagement.baseUrl)
    },
    webViews: {
      addressBook: routeHelper.externalWithParams<{ token: string; username: string }>(
        `${APP_CONFIG.shared.routes.webViews.baseUrl}/address-book`
      ),
      requestConcierge: routeHelper.externalWithParams<{ manheimAccountNumber?: string; username: string }>(
        `${APP_CONFIG.shared.routes.webViews.baseUrl}/order-concierge`
      )
    },
    legacySellerDashboard: {
      admin: {
        accountSelect: routeHelper.external(`${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/admin`),
        accountGroupConfig: routeHelper.external(
          `${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/admin/account_group_configs`
        ),
        accountRollupConfig: routeHelper.external(
          `${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/admin/account_groups`
        )
      },
      auctionInventory: routeHelper.externalWithParams<{ 'f.operatingLocationNames'?: string }>(
        `${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/auction/auctioninventory/inventory/show`
      ),
      expressDirect: routeHelper.external(`${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/ui/direct_sales`),
      import: routeHelper.external(`${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/ui/import`),
      inventory: routeHelper.externalWithParams<{ searchText?: string }>(
        `${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/ui/inventory/show`
      ),
      listingManager: routeHelper.externalWithParams<{ searchText?: string; unitHref?: string }>(
        `${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/ui/listing_manager`
      ),
      registration: routeHelper.external(`${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/ui/registration`),
      sales: routeHelper.external(`${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/ui/sales`),
      sellingCenter: routeHelper.external(`${APP_CONFIG.shared.routes.sellerDashboardLegacy.baseUrl}/ui/overview`)
    },
    inventory: {
      auctionRuns: routeHelper.external(`${APP_CONFIG.shared.routes.inventory.baseUrl}/view_runs`),
      inventoryManagerClassic: routeHelper.external(`${APP_CONFIG.shared.routes.inventory.baseUrl}/view_runs`),
      search: routeHelper.external(`${APP_CONFIG.shared.routes.inventory.baseUrl}/inventory/search`),
      quickLinks: routeHelper.external(
        `${APP_CONFIG.shared.routes.inventory.baseUrl}/listing_wizard/select_entry_method`
      )
    },
    mashery: {
      unit: routeHelper.externalWithParams<{ guid: string }>(
        `${APP_CONFIG.shared.routes.mashery.baseUrl}/units/id/:guid`
      )
    },
    oauth: {
      login: routeHelper.externalWithParams<ManheimLoginQueryParams>(APP_CONFIG.shared.routes.oauth.loginBaseUrl),
      logout: routeHelper.externalWithParams<{ TargetResource: string; InErrorResource?: string }>(
        APP_CONFIG.shared.routes.oauth.logoutBaseUrl
      )
    },
    ove: {
      listing: routeHelper.externalWithParams<{ vin: string }>(
        `${APP_CONFIG.listingManager.oveBaseUrl}/members/results#/details/:vin/OVE`
      )
    }
  }
} as const;
